import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import StartTrialModal from '../../components/subscription/StartTrialModal';
import UpgradeModalEmbedded from '../../components/subscription/UpgradeModalEmbedded';
import CustomerPortalEmbedded from '../../components/subscription/CustomerPortalEmbedded';
import PaymentHistory from '../../components/subscription/PaymentHistory';
import InvoiceManagement from '../../components/subscription/InvoiceManagement';
import { FaCreditCard, FaCheck, FaArrowUp } from 'react-icons/fa';
import './SubscriptionManagement.css';

function SubscriptionManagement() {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Check for successful return from Stripe Checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // Clear the session_id from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success message (subscription webhook will update the actual status)
      setError('');
      setSuccessMessage('Payment successful! Your subscription is being updated...');
      
      // Add a slight delay to allow webhook to process
      setTimeout(() => {
        fetchSubscriptionData();
        // Clear success message after data is refreshed
        setTimeout(() => setSuccessMessage(''), 5000);
      }, 2000);
    } else {
      fetchSubscriptionData();
    }
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscription status
      const statusResponse = await apiClient.get('/subscription/status');
      setSubscriptionStatus(statusResponse.data);
      
      // Fetch available plans
      const plansResponse = await apiClient.get('/paywall/subscriptions/plans');
      setAvailablePlans(plansResponse.data);
      
      // Set current plan details
      if (statusResponse.data.plan_id) {
        const plan = plansResponse.data.find(p => p.id === statusResponse.data.plan_id);
        setCurrentPlan(plan);
      } else if (statusResponse.data.plan_name) {
        // If we have the plan name directly, create a plan object
        setCurrentPlan({
          id: statusResponse.data.plan_id,
          name: statusResponse.data.plan_name
        });
      }
      
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      if (error.response && error.response.status === 404) {
        setError('Subscription endpoints not found. Please contact support.');
      } else if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to load subscription information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = () => {
    setShowTrialModal(true);
  };

  const handleTrialStarted = () => {
    fetchSubscriptionData();
  };

  const handleUpgradePlan = (plan) => {
    // Show upgrade modal with selected plan
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const handleManageSubscription = () => {
    // Open embedded Customer Portal modal
    setShowPortalModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="subscription-management">
        <div className="loading-spinner">Loading subscription information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-management">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const { status, trial_days_remaining } = subscriptionStatus || {};
  const isTrial = status === 'trial';
  const isActive = status === 'active';

  return (
    <div className="subscription-management">
      <div className="subscription-header">
        <h2>Subscription Management</h2>
        <p>Manage your subscription plan and billing</p>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Current Subscription Status */}
      <div className="subscription-status-card">
        <h3>Current Subscription</h3>
        
        {!status || status === 'none' ? (
          <div className="no-subscription">
            <p>You don't have an active subscription.</p>
            <button 
              className="start-trial-button"
              onClick={handleStartTrial}
            >
              Start Free Trial
            </button>
          </div>
        ) : (
          <div className="subscription-details">
            <div className="subscription-info">
              <div className="info-item">
                <span className="info-label">Plan:</span>
                <span className="info-value">{currentPlan?.name || 'Unknown'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`status-badge status-${status}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
              </div>
              
              {isTrial && (
                <div className="info-item">
                  <span className="info-label">Trial Ends:</span>
                  <span className="info-value">
                    {trial_days_remaining} days remaining
                  </span>
                </div>
              )}
              
              {isActive && subscriptionStatus.next_billing_date && (
                <div className="info-item">
                  <span className="info-label">Next Billing:</span>
                  <span className="info-value">
                    {formatDate(subscriptionStatus.next_billing_date)}
                  </span>
                </div>
              )}
            </div>
            
            {(isTrial || isActive) && (
              <div className="subscription-actions">
                {isTrial ? (
                  <button 
                    className="upgrade-button"
                    onClick={() => {
                      const upgradePlan = availablePlans.find(p => p.price_monthly > 0);
                      if (upgradePlan) handleUpgradePlan(upgradePlan);
                    }}
                  >
                    <FaArrowUp /> Upgrade to Paid Plan
                  </button>
                ) : (
                  <button 
                    className="manage-button"
                    onClick={handleManageSubscription}
                  >
                    <FaCreditCard /> Manage Subscription
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div className="available-plans">
        <h3>Available Plans</h3>
        <div className="plans-grid">
          {availablePlans.map(plan => {
            const isCurrentPlan = currentPlan?.id === plan.id;
            const canUpgrade = currentPlan && plan.price_monthly > currentPlan.price_monthly;
            
            return (
              <div 
                key={plan.id} 
                className={`plan-card ${isCurrentPlan ? 'current' : ''}`}
              >
                {isCurrentPlan && (
                  <div className="current-plan-badge">Current Plan</div>
                )}
                
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  ${plan.price_monthly}
                  <span>/month</span>
                </div>
                
                <ul className="plan-features">
                  {plan.features && Object.entries(plan.features).map(([key, value]) => (
                    <li key={key}>
                      <FaCheck />
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {
                        value === null ? 'Unlimited' : value
                      }
                    </li>
                  ))}
                </ul>
                
                {!isCurrentPlan && (
                  <button 
                    className={`plan-action-button ${canUpgrade ? 'upgrade' : 'select'}`}
                    onClick={() => handleUpgradePlan(plan)}
                    disabled={isCurrentPlan}
                  >
                    {canUpgrade ? 'Upgrade' : 'Select Plan'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Invoice */}
      {isActive && <InvoiceManagement />}

      {/* Payment History */}
      {(isActive || isTrial) && <PaymentHistory />}

      {/* Start Trial Modal */}
      <StartTrialModal 
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        onTrialStarted={handleTrialStarted}
      />

      {/* Upgrade Modal with embedded payment */}
      <UpgradeModalEmbedded
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        plan={selectedPlan}
        onSuccess={() => {
          setShowUpgradeModal(false);
          fetchSubscriptionData();
        }}
      />

      {/* Customer Portal Modal with embedded components */}
      <CustomerPortalEmbedded
        isOpen={showPortalModal}
        onClose={() => {
          setShowPortalModal(false);
          // Refresh subscription data when portal closes
          fetchSubscriptionData();
        }}
      />
    </div>
  );
}

export default SubscriptionManagement;